import React, {useEffect, useState} from "react";
import {Button, Modal, Spinner} from "react-bootstrap";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import {ToastContainer} from "react-toastify";
import "../../../../css/SettingsForms.css";

const emptyAccount = {
  name: "", smtpHost: "", smtpPort: 587, username: "", password: "", senderAddress: "", senderName: "",
  securityMode: "STARTTLS", authenticationEnabled: true, active: true, defaultForSchedules: false
};

const MailConfiguration = () => {
  const axiosPrivate = useAxiosPrivate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems((await axiosPrivate.get("/mail-configuration")).data || []); }
    catch (e) { toastError("Nie udało się pobrać konfiguracji poczty"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setTestRecipient(""); setEditing({...emptyAccount}); };
  const openEdit = item => { setTestRecipient(""); setEditing({...item, password: ""}); };
  const setField = (field, value) => setEditing(prev => ({...prev, [field]: value}));

  const save = async () => {
    if (!editing.name?.trim() || !editing.smtpHost?.trim() || !editing.senderAddress?.trim() || !editing.smtpPort) {
      toastError("Uzupełnij nazwę, host, port SMTP i adres nadawcy"); return;
    }
    setSaving(true);
    try {
      if (editing.id) await axiosPrivate.put(`/mail-configuration/${editing.id}`, editing);
      else await axiosPrivate.post("/mail-configuration", editing);
      toastSuccess("Konfiguracja skrzynki została zapisana");
      setEditing(null); await load();
    } catch (e) { toastError(typeof e?.response?.data === "string" ? e.response.data : "Nie udało się zapisać skrzynki"); }
    finally { setSaving(false); }
  };

  const remove = async item => {
    if (!window.confirm(`Usunąć skrzynkę „${item.name}”?`)) return;
    try { await axiosPrivate.delete(`/mail-configuration/${item.id}`); toastSuccess("Skrzynka została usunięta"); await load(); }
    catch (e) { toastError("Nie udało się usunąć skrzynki"); }
  };

  const sendTest = async () => {
    if (!editing) return;
    if (!testRecipient.trim()) { toastError("Podaj adres odbiorcy wiadomości testowej"); return; }
    if (!editing.smtpHost?.trim() || !editing.senderAddress?.trim() || !editing.smtpPort) {
      toastError("Uzupełnij host, port SMTP i adres nadawcy"); return;
    }
    setTesting(true);
    try {
      const response = await axiosPrivate.post("/mail-configuration/test", {account: editing, recipient: testRecipient.trim()});
      toastSuccess(response?.data?.message || "Wiadomość testowa została wysłana");
    } catch (e) {
      const data = e?.response?.data;
      const message = typeof data === "string" ? data : data?.message;
      toastError(message || "Test wysyłki nie powiódł się");
    } finally { setTesting(false); }
  };

  const securityWarning = editing && (
    (Number(editing.smtpPort) === 587 && editing.securityMode === "SSL_TLS") ||
    (Number(editing.smtpPort) === 465 && editing.securityMode === "STARTTLS")
  );

  return <div id="layoutSidenav_content" className="settings-page">
    <AppComponentCardHeader title="Konfiguracja poczty"/>
    <div className="container-fluid px-4 py-3 settings-page-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted">Skrzynki poczty wychodzącej SMTP. Jedną skrzynkę można wskazać jako domyślną dla grafików.</div>
        <Button size="sm" onClick={openNew}>Dodaj skrzynkę</Button>
      </div>
      {loading ? <div className="text-center py-4"><Spinner animation="border" size="sm"/></div> :
        <div className="table-responsive"><table className="table table-sm align-middle">
          <thead><tr><th>Nazwa</th><th>Adres nadawcy</th><th>Serwer SMTP</th><th>Zabezpieczenie</th><th>Grafiki</th><th>Status</th><th></th></tr></thead>
          <tbody>{items.length === 0 ? <tr><td colSpan="7" className="text-muted text-center py-4">Nie skonfigurowano żadnej skrzynki.</td></tr> : items.map(item =>
            <tr key={item.id}><td>{item.name}</td><td>{item.senderAddress}</td><td>{item.smtpHost}:{item.smtpPort}</td><td>{item.securityMode === "SSL_TLS" ? "SSL/TLS" : item.securityMode}</td>
              <td>{item.defaultForSchedules ? <strong>Domyślna</strong> : "—"}</td><td>{item.active ? "Aktywna" : "Nieaktywna"}</td>
              <td className="text-end text-nowrap"><Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(item)}>Edytuj</Button><Button variant="outline-danger" size="sm" onClick={() => remove(item)}>Usuń</Button></td></tr>)}</tbody>
        </table></div>}
    </div>

    <Modal show={!!editing} onHide={() => !saving && setEditing(null)} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>{editing?.id ? "Edycja skrzynki" : "Nowa skrzynka"}</Modal.Title></Modal.Header>
      <Modal.Body>{editing && <div className="row g-3">
        <div className="col-md-6"><label className="form-label">Nazwa konfiguracji</label><input className="form-control" value={editing.name || ""} onChange={e => setField("name", e.target.value)}/></div>
        <div className="col-md-6"><label className="form-label">Nazwa nadawcy wiadomości</label><input className="form-control" value={editing.senderName || ""} onChange={e => setField("senderName", e.target.value)} placeholder="np. Qstaff – Firma"/></div>
        <div className="col-md-8"><label className="form-label">Host SMTP</label><input className="form-control" value={editing.smtpHost || ""} onChange={e => setField("smtpHost", e.target.value)}/></div>
        <div className="col-md-4"><label className="form-label">Port SMTP</label><input type="number" min="1" max="65535" className="form-control" value={editing.smtpPort || ""} onChange={e => setField("smtpPort", Number(e.target.value))}/></div>
        <div className="col-md-6"><label className="form-label">Adres nadawcy</label><input type="email" className="form-control" value={editing.senderAddress || ""} onChange={e => setField("senderAddress", e.target.value)}/></div>
        <div className="col-md-6"><label className="form-label">Login SMTP</label><input className="form-control" value={editing.username || ""} onChange={e => setField("username", e.target.value)} placeholder="Pusty = adres nadawcy"/></div>
        <div className="col-md-6"><label className="form-label">Hasło {editing.passwordConfigured && <span className="text-success fw-normal">(hasło zapisane)</span>}</label><input type="password" autoComplete="new-password" className="form-control" value={editing.password || ""} onChange={e => setField("password", e.target.value)} placeholder={editing.passwordConfigured ? "Pozostaw puste, aby nie zmieniać" : ""}/></div>
        <div className="col-md-6"><label className="form-label">Zabezpieczenie</label><select className="form-select" value={editing.securityMode || "STARTTLS"} onChange={e => setField("securityMode", e.target.value)}><option value="STARTTLS">STARTTLS</option><option value="SSL_TLS">SSL/TLS</option><option value="NONE">Brak</option></select></div>
        {securityWarning && <div className="col-12"><div className="alert alert-warning py-2 mb-0">Wybrany port i sposób zabezpieczenia są nietypowym połączeniem. Sprawdź ustawienia wymagane przez serwer SMTP.</div></div>}
        <div className="col-12 d-flex gap-4 flex-wrap"><label><input type="checkbox" className="me-2" checked={!!editing.authenticationEnabled} onChange={e => setField("authenticationEnabled", e.target.checked)}/>Uwierzytelnianie SMTP</label><label><input type="checkbox" className="me-2" checked={!!editing.active} onChange={e => setEditing(prev => ({...prev, active: e.target.checked, defaultForSchedules: e.target.checked ? prev.defaultForSchedules : false}))}/>Skrzynka aktywna</label><label className={!editing.active ? "text-muted" : ""}><input type="checkbox" className="me-2" checked={!!editing.defaultForSchedules} disabled={!editing.active} onChange={e => setField("defaultForSchedules", e.target.checked)}/>Domyślna skrzynka dla grafików</label></div>
        <div className="col-12"><hr className="my-1"/><label className="form-label mt-2">Adres odbiorcy wiadomości testowej</label><div className="d-flex gap-2"><input type="email" className="form-control" value={testRecipient} onChange={e => setTestRecipient(e.target.value)} placeholder="adres@domena.pl"/><Button variant="outline-secondary" className="text-nowrap" onClick={sendTest} disabled={testing}>{testing ? "Testowanie..." : "Testuj konfigurację"}</Button></div><div className="form-text">Test używa aktualnych wartości z tego okna. Nie musisz najpierw zapisywać zmian.</div></div>
      </div>}</Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Anuluj</Button><Button onClick={save} disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button></Modal.Footer>
    </Modal>

    <ToastContainer/>
  </div>;
};

export default MailConfiguration;
